import type { ApiError } from '../errors/ApiError';
import type { GeneratorResult, GeneratorTemplate } from '../TeoApi';
import {
  cleanSelectedOptionsForTemplate,
  optionsToDropdownCategories,
  processSelectedOptionsForTemplate,
} from './GeneratorUiHandler.helpers';

export class GeneratorHistoryEntry {
  // static override _tag = 'GeneratorHistoryEntry' as const;
  // static {
  //   this.register();
  // }

  instructions: string;
  selectedOptions: Record<string, string | undefined>;
  generatorResult: GeneratorResult | null;
  _template: GeneratorTemplate | null;

  constructor(initialState?: Partial<GeneratorHistoryEntry>) {
    // this will be overwritten when hydrating!
    this.instructions = initialState?.instructions || '';
    this.selectedOptions = initialState?.selectedOptions || {};
    this.generatorResult = initialState?.generatorResult || null;
    this._template = initialState?.template || null;
    this.template = this._template;
  }

  clone(): GeneratorHistoryEntry {
    return new GeneratorHistoryEntry({
      instructions: this.instructions,
      selectedOptions: { ...this.selectedOptions },
      template: this.template,
      generatorResult: this.generatorResult,
    });
  }

  get template(): GeneratorTemplate | null {
    return this._template;
  }

  set template(template: GeneratorTemplate | null) {
    if (template) {
      processSelectedOptionsForTemplate(template, this.selectedOptions);
    }
    this._template = template;
  }
}

export interface GeneratorStateExported {
  templates: {
    available: GeneratorTemplate[];
  };
  history: GeneratorHistoryEntry[];
  editingHistory: GeneratorHistoryEntry[];
  selectedHistory: number;
}

export class GeneratorState {
  // static override _tag = 'GeneratorState' as const;
  // static {
  //   this.register();
  // }

  templates: {
    loading: boolean;
    available: GeneratorTemplate[];
    error?: ApiError | null;
  } = {
    loading: false,
    available: [],
    error: null,
  };

  history: GeneratorHistoryEntry[] = [];
  editingHistory: GeneratorHistoryEntry[] = [new GeneratorHistoryEntry()];

  selectedHistory: number = 0;

  loading: boolean = false;

  constructor(initialState?: GeneratorStateExported) {
    if (initialState) {
      this.templates = {
        loading: false,
        available: initialState.templates.available,
        error: null,
      };
      this.history = initialState.history.map((h) => new GeneratorHistoryEntry(h));
      this.editingHistory = initialState.editingHistory.map((h) => new GeneratorHistoryEntry(h));
      this.selectedHistory = initialState.selectedHistory;
    }
  }

  toJSON() {
    // don't persist loading
    const { loading, ...state } = this;
    return state;
  }

  get selectedOptions(): Record<string, string | undefined> {
    return this.currentHistory.selectedOptions ?? {};
  }

  getSelectedOptionsForRequest(): Record<string, string> {
    return cleanSelectedOptionsForTemplate(
      this.currentHistory.template,
      this.currentHistory.selectedOptions,
    );
  }

  selectOption(id: string, value: number | string) {
    if (!this.currentHistory) return;
    this.currentHistory.selectedOptions = {
      ...this.currentHistory.selectedOptions,
      [id]: String(value),
    };
  }

  get allSelected(): boolean {
    const dropdownCategories = this.dropdownCategories;
    const selectedOptions = this.selectedOptions;
    if (dropdownCategories.length === 0) {
      return false;
    }
    for (const category of dropdownCategories) {
      if (selectedOptions[category.id] === undefined) {
        return false;
      }
    }
    return true;
  }

  get options() {
    return this.currentHistory.template?.options || {};
  }

  get dropdownCategories() {
    return optionsToDropdownCategories(this.currentTemplate?.options);
  }

  get currentTemplate(): GeneratorTemplate | null {
    return this.currentHistory.template || null;
  }

  get currentHistory(): GeneratorHistoryEntry {
    let entry = this.editingHistory[this.selectedHistory];
    if (entry) return entry;

    this.selectedHistory = 0;
    entry = this.editingHistory[0];
    if (entry) return entry;

    entry = new GeneratorHistoryEntry();
    this.editingHistory.push(entry);
    this.selectedHistory = this.editingHistory.length - 1;
    return entry;
  }
}
